import styled from 'styled-components';
import { useState, useEffect } from 'react';
import { priceFormat, roundPrice } from '../priceFormat';
import RegularPrices from '../models/regularPrices.model';
import Publications from '../models/publications.model';

interface Props{
  // eslint-disable-next-line @typescript-eslint/ban-types
  prices:RegularPrices;
  publications: Publications
  avgTransferences: number
  showTransferencesPrice: boolean
}

export default function MarketPriceRange(props:Readonly<Props>) {
  const {
    prices, publications, avgTransferences, showTransferencesPrice,
  } = props;

  const publicationsList = publications.list || [];
  const minimunPrice = publicationsList.length ? Math.min(...publicationsList.map((publication) => publication.precio || 0)) : 0;
  const maximunPrice = publicationsList.length ? Math.max(...publicationsList.map((publication) => publication.precio || 0)) : 0;
  const averagePublications = publications.price_average || 0;

  const [minValue, setMinValue] = useState(0);
  const [maxValue, setMaxValue] = useState(0);

  useEffect(() => {
    const allValues = [
      minimunPrice ?? 0,
      maximunPrice ?? 0,
      averagePublications ?? 0,
      avgTransferences ?? 0,
      prices.min_value ?? 0,
      prices.max_value ?? 0,
      prices.autofact_value ?? 0,
    // eslint-disable-next-line no-restricted-globals
    ].filter((value) => typeof value === 'number' && !isNaN(value) && value > 0); // Filtra solo números válidos

    if (allValues.length > 0) {
      const min = Math.min(...allValues);
      const max = Math.max(...allValues);

      setMinValue(min);
      setMaxValue(max);
    } else {
      setMinValue(0);
      setMaxValue(0);
    }
  }, [minimunPrice, maximunPrice, averagePublications, avgTransferences, prices]);

  const range = maxValue - minValue;
  let percentageAvgTransferences = 0;
  let percentageAvgPublications = 0;
  let positionAvgPublications = 0;
  let positionAvgTransferences: number;
  let percentageMinimunPublications = 0;
  let percentageMaximunPublications = 0;

  const calculatePosition = (percentage: number) => Math.max(1, Math.min(9, Math.round(percentage / 10)));

  if (publicationsList.length) {
    const difAvgPublications = averagePublications - minValue;
    percentageAvgPublications = Math.round((difAvgPublications * 100) / range);
    positionAvgPublications = calculatePosition(percentageAvgPublications);

    // calcular la posición del min y max valor de publicaciones
    const difMinimunPublications = minimunPrice - minValue;
    percentageMinimunPublications = Math.round((difMinimunPublications * 100) / range);

    const difMaximunPublications = maximunPrice - minValue;
    percentageMaximunPublications = Math.round((difMaximunPublications * 100) / range);
  }
  if (avgTransferences) {
    const difAvgTransferences = avgTransferences - minValue;
    percentageAvgTransferences = Math.round((difAvgTransferences * 100) / range);
    positionAvgTransferences = calculatePosition(percentageAvgTransferences);

    if (positionAvgPublications && positionAvgPublications === positionAvgTransferences) {
      positionAvgTransferences = positionAvgTransferences > positionAvgPublications
        ? positionAvgTransferences + 1
        : positionAvgTransferences - 1;
    }
  }

  // calcular posición precios recomendados autofact
  const difRecommendedMinValue = prices.min_value ? prices.min_value - minValue : 0;
  const difRecommendedMaxValue = prices.max_value ? prices.max_value - minValue : 0;
  const difRecommendedAfValue = prices.autofact_value ? prices.autofact_value - minValue : 0;

  const percentageRecommendedMinValue = Math.round((difRecommendedMinValue * 100) / range);
  const percentageRecommendedMaxValue = Math.round((difRecommendedMaxValue * 100) / range);
  const percentageRecommendedAfValue = Math.round((difRecommendedAfValue * 100) / range);

  const renderBoxes = () => {
    const boxesTop = [];
    const boxesBottom = [];

    const customDataMap = new Map();

    customDataMap.set(`recommended-${percentageRecommendedMinValue}`, {
      title: 'Precio recomendado más bajo',
      value: `$${priceFormat(roundPrice(prices.min_value ? prices.min_value : 0, 5))}`,
      isRecommendedPrice: true,
    });

    customDataMap.set(`recommended-${percentageRecommendedMaxValue}`, {
      title: 'Precio recomendado más alto',
      value: `$${priceFormat(roundPrice(prices.max_value ? prices.max_value : 0, 5))}`,
      isRecommendedPrice: true,
    });

    customDataMap.set(`recommended-${percentageRecommendedAfValue}`, {
      title: 'Precio recomendado Autofact',
      value: `$${priceFormat(roundPrice(prices.autofact_value ? prices.autofact_value : 0, 5))}`,
      isRecommendedPrice: true,
    });

    if (publicationsList.length) {
      customDataMap.set(`publication-${percentageMinimunPublications}`, {
        title: 'Menor precio publicado',
        value: `$${priceFormat(roundPrice(minimunPrice, 5))}`,
        isRecommendedPrice: false,
      });

      customDataMap.set(`publication-${percentageMaximunPublications}`, {
        title: 'Mayor precio publicado',
        value: `$${priceFormat(roundPrice(maximunPrice, 5))}`,
        isRecommendedPrice: false,
      });

      customDataMap.set(`publication-${percentageAvgPublications}`, {
        title: 'Promedio precio publicaciones',
        value: `$${priceFormat(roundPrice(averagePublications, 5))}`,
        isRecommendedPrice: false,
      });
    }

    if (showTransferencesPrice) {
      customDataMap.set(`transference-${percentageAvgTransferences}`, {
        title: 'Promedio ventas reales',
        value: `$${priceFormat(roundPrice(avgTransferences, 5))}`,
        isRecommendedPrice: false,
      });
    }

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i <= 100; i++) {
      // Posiciones intermedias (0 a 10): Verificar datos personalizados
      const customDataRecommended = customDataMap.get(`recommended-${i}`);
      const customDataPublication = customDataMap.get(`publication-${i}`);
      const customDataTransference = customDataMap.get(`transference-${i}`);
      if (customDataRecommended) {
        boxesTop.push(
          <RecommendedPrice
            key={i}
            title={customDataRecommended.title}
            value={customDataRecommended.value}
            showData={customDataRecommended.isRecommendedPrice}
          />,
        );
      } else if (customDataPublication) {
        boxesTop.push(
          <PublicationPrice
            key={i}
            title={customDataPublication.title}
            value={customDataPublication.value}
            showData={customDataPublication.isRecommendedPrice}
          />,
        );
      } else if (customDataTransference) {
        boxesTop.push(
          <PublicationPrice
            key={i}
            title={customDataTransference.title}
            value={customDataTransference.value}
          />,
        );
      } else {
        boxesTop.push(<BoxEmpty key={i} />);
      }
    }

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i <= 100; i++) {
      // Posiciones intermedias (0 a 10): Verificar datos personalizados
      const customDataRecommended = customDataMap.get(`recommended-${i}`);
      const customDataPublication = customDataMap.get(`publication-${i}`);
      const customDataTransference = customDataMap.get(`transference-${i}`);
      if (customDataPublication) {
        // Si hay datos personalizados para esta posición
        boxesBottom.push(
          <PublicationPrice
            key={i}
            title={customDataPublication.title}
            value={customDataPublication.value}
            showData={!customDataPublication.isRecommendedPrice}
          />,
        );
      } else if (customDataTransference) {
        boxesBottom.push(
          <PublicationPrice
            key={i}
            title={customDataTransference.title}
            value={customDataTransference.value}
            showData={!customDataTransference.isRecommendedPrice}
          />,
        );
      } else if (customDataRecommended) {
        boxesBottom.push(
          <RecommendedPrice
            key={i}
            title={customDataRecommended.title}
            value={customDataRecommended.value}
            showData={!customDataRecommended.isRecommendedPrice}
          />,
        );
      } else {
        boxesBottom.push(<EmtyBoxContainer key={i} />);
      }
    }

    return [boxesTop, boxesBottom];
  };

  const isMobile = window.innerWidth <= 700;
  const recommendedBoxes = renderBoxes();

  return (
    <ContainerParent>
      <Container>
        <SubCont6>
          <SubCont3>
            {isMobile ? recommendedBoxes[0].slice().reverse() : recommendedBoxes[0]}
          </SubCont3>
          <SubCont1>
            <BarContainer />
          </SubCont1>
          <SubCont4>
            {isMobile ? recommendedBoxes[1].slice().reverse() : recommendedBoxes[1]}
          </SubCont4>
        </SubCont6>
      </Container>
    </ContainerParent>
  );
}

function RecommendedPrice({ title, value, showData }: { title: string, value: string | null, showData: boolean }) {
  return (
    <AutoIconTextContainer>
      <TitleValueContainer>
        <TitlePriceRecommended showData={showData}>
          {title}
        </TitlePriceRecommended>
        <BlankSpace width="0px" height="0px" />
        <PriceText showData={showData}>
          {value}
        </PriceText>
        <BlankSpace width="0px" height="5px" />
      </TitleValueContainer>
      { showData ? <IndicatorTriangleTop /> : '' }
    </AutoIconTextContainer>
  );
}

function PublicationPrice({
  title, value, showData,
}: { title: string, value: string | null, showData?: boolean }) {
  return (
    <AutoIconTextContainer2>
      { showData ? <IndicatorTriangleBottom /> : '' }
      <TitleValueContainer2>
        <BlankSpace width="0px" height="5px" />
        <PriceText showData={showData}>
          {value}
        </PriceText>
        <TitlePrice showData={showData}>
          {title}
        </TitlePrice>
      </TitleValueContainer2>
    </AutoIconTextContainer2>
  );
}

function BoxEmpty() {
  return (
    <EmtyBoxContainer />
  );
}

const TitleValueContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  @media (max-width: 700px) {
    flex-direction: column;
    align-items: flex-end;
    justify-content: flex-end;
    width: auto;
    margin: 1px 0;
  }
`;

const TitleValueContainer2 = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  @media (max-width: 700px) {
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    width: auto;
    margin: 1px 0;
  }
`;

const TitlePrice = styled.div<{showData?: boolean}>`
  font-size: 10px;
  text-align: center;
  color: ${(props) => (props.showData ? '#3D5176' : 'transparent')};
  @media (max-width: 700px) {
    text-align: left;
  }
`;

const TitlePriceRecommended = styled.div<{showData?: boolean}>`
  font-size: 10px;
  text-align: center;
  color: ${(props) => (props.showData ? '#3D5176' : 'transparent')};
  font-family: 'Roboto bold';
  @media (max-width: 700px) {
    text-align: right;
  }
`;

const PriceText = styled.div<{showData?: boolean}>`
  font-size: 10px;
  text-align: center;
  color: ${(props) => (props.showData ? '#3D5176' : '')};
  font-family: 'Roboto bold';
`;

const Container = styled.div`
  padding: 10px 0;
  display: flex;
  flex-wrap: nowrap;
  @media (max-width: 700px) {
    flex-direction: column;
    align-items: center;
  }
`;

const ContainerParent = styled.div`
  align-items: center;
`;

const SubCont6 = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  @media (max-width: 700px) {
    flex-direction: row;
    align-items: stretch;
    justify-content: center;
    height: 350px;
  }
`;

const SubCont3 = styled.div`
  width: 90%;
  min-width: 250px;
  display: flex;
  flex-direction: row;
  margin: auto;
  justify-content: space-between;
  @media (max-width: 700px) {
    flex-direction: column;
    width: auto;
    min-width: unset;
    height: 100%;
    margin: 0px;
    justify-content: flex-end;
    align-items: flex-end;
    flex: 1;
  }
`;

const SubCont4 = styled.div`
  width: 90%;
  min-width: 250px;
  display: flex;
  flex-direction: row;
  margin: auto;
  justify-content: space-between;
  @media (max-width: 700px) {
    flex-direction: column;
    width: auto;
    min-width: unset;
    height: 100%;
    margin: 0px;
    justify-content: flex-start;
    align-items: flex-start;
    flex: 1;
  }
`;

const SubCont1 = styled.div`
  width: 90%;
  min-width: 250px;
  display: flex;
  flex-direction: row;
  margin: auto;
  justify-content: center;
  @media (max-width: 700px) {
    flex-direction: column;
    width: auto;
    min-width: unset;
    margin: 0 10px;
    justify-content: center;
    align-items: center;
    height: 100%;
    flex: 0 0 32px;
    display: flex;
  }
`;

const BarContainer = styled.div`
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 9px;
  font-weight: bold;
  background: linear-gradient(to right, #99B7E8, #1A5ECC);
  border-radius: 10px;
  width: 98%;
  margin: 5px auto;
  @media (max-width: 700px) {
    width: 16px;
    height: 560px;
    min-height: unset;
    background: linear-gradient(to top, #99B7E8, #1A5ECC);
    flex-direction: column;
    border-radius: 10px;
    min-width: 16px;
    max-width: 16px;
  }
`;

const AutoIconTextContainer = styled.div<{showData?: boolean}>`
  width: 2px;
  margin-left: 30px;
  margin-right: 30px;
  color: ${(props) => (props.showData ? '#3D5176' : 'transparent')};
  display: flex;
  flex-direction: column;
  align-items: center;
  @media (max-width: 700px) {
    flex-direction: row;
    align-items: flex-end;
    justify-content: flex-end;
    width: auto;
    margin: 1px 0;
  }
`;

const AutoIconTextContainer2 = styled.div<{showData?: boolean}>`
  width: 2px;
  margin-left: 30px;
  margin-right: 30px;
  color: ${(props) => (props.showData ? '#3D5176' : 'transparent')};
  display: flex;
  flex-direction: column;
  align-items: center;
  @media (max-width: 700px) {
    flex-direction: row;
    align-items: flex-start;
    justify-content: flex-start;
    width: auto;
    margin: 1px 0;
  }
`;

const EmtyBoxContainer = styled.div`
  width: 0px;
  margin: 0px 0px;
  display: flex;
  flex-direction: column;
  align-items: center;
  @media (max-width: 700px) {
    flex-direction: row;
    align-items: flex-end;
    justify-content: flex-end;
    width: auto;
    margin: 1px 0;
  }
`;

const IndicatorTriangleTop = styled.div`
  width: 0;
  height: 0;
  margin: auto;
  border-right: 6px solid transparent;
  border-left: 6px solid transparent;
  border-top: 6px solid #14346f;
  border-bottom: 0px solid transparent;
  @media (max-width: 700px) {
    border-top: none;
    border-left: 6px solid #14346f;
    border-bottom: 6px solid transparent;
    border-right: none;
    border-top: 6px solid transparent;
    margin-left: 10px;
  }
`;

const IndicatorTriangleBottom = styled.div`
  width: 0;  
  height: 0;
  margin: auto;
  border-right: 6px solid transparent;
  border-left: 6px solid transparent;
  border-top: 0px solid transparent;
  border-bottom: 8px solid #14346f;
  @media (max-width: 700px) {
    border-bottom: none;
    border-left: 8px solid transparent;
    border-top: 6px solid transparent;
    border-right: 6px solid #14346f;
    border-bottom: 6px solid transparent;
    margin-right: 10px;
  }
`;

export const BlankSpace = styled.div<{width?:string, height?:string}>`
  width: ${(props) => props.width || '100%'};
  height: ${(props) => props.height || '100%'};
`;
